// src/pages/finance-portal/ic-reconciliation/IcReconciliation.js
//
// IC Balance Reconciliation: scan + breaks list + resolve/dismiss

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, Chip, Alert, Skeleton, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import RadarIcon from '@mui/icons-material/Radar';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_TABS = ['ALL', 'OPEN', 'RESOLVED', 'DISMISSED'];

const STATUS_COLORS = {
    OPEN:      { bg: '#fef9c3', color: '#854d0e' },
    RESOLVED:  { bg: '#dcfce7', color: '#166534' },
    DISMISSED: { bg: '#f1f5f9', color: '#94a3b8' },
};

const KIND_COLORS = {
    UNMATCHED:       { bg: '#fecaca', color: '#991b1b' },
    AMOUNT_MISMATCH: { bg: '#fed7aa', color: '#9a3412' },
    FX_DIFFERENCE:   { bg: '#dbeafe', color: '#1d4ed8' },
    TIMING:          { bg: '#fef3c7', color: '#a16207' },
    OTHER:           { bg: '#f1f5f9', color: '#475569' },
};

function fmt(val, currency = 'USD') {
    if (val == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 2,
    }).format(val);
}

export default function IcReconciliation() {
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusTab, setStatusTab] = useState(0);
    const [kindFilter, setKindFilter] = useState('');

    const [scanDialog, setScanDialog] = useState({
        open: false, period_start: '', period_end: '',
        tolerance: '0.01', purge_open: true,
    });

    const [resolveDialog, setResolveDialog] = useState({
        open: false, breakItem: null, action: 'resolve', notes: '',
    });

    const fetchBreaks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const status = STATUS_TABS[statusTab];
            if (status !== 'ALL') params.status = status;
            if (kindFilter) params.kind = kindFilter;
            const res = await axiosInstance.get('/api/finance/ic-recon/breaks', { params });
            if (res.data.success) {
                setRows(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, statusTab, kindFilter]);

    useEffect(() => { fetchBreaks(); }, [fetchBreaks]);

    const submitScan = async () => {
        if (!scanDialog.period_start || !scanDialog.period_end) {
            enqueueSnackbar('Period start and end are required', { variant: 'warning' });
            return;
        }
        try {
            const res = await axiosInstance.post('/api/finance/ic-recon/scan', {
                period_start: scanDialog.period_start,
                period_end: scanDialog.period_end,
                tolerance: parseFloat(scanDialog.tolerance) || 0,
                purge_open: !!scanDialog.purge_open,
            });
            enqueueSnackbar(res.data?.message || 'Scan complete', { variant: 'success' });
            setScanDialog({ ...scanDialog, open: false });
            fetchBreaks();
        } catch (err) {
            const errs = err.response?.data?.errors;
            if (errs) {
                enqueueSnackbar('Validation: ' + Object.values(errs).join(', '), { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
            }
        }
    };

    const submitResolve = async () => {
        try {
            const path = resolveDialog.action === 'resolve' ? 'resolve' : 'dismiss';
            const res = await axiosInstance.post(
                `/api/finance/ic-recon/breaks/${resolveDialog.breakItem.id}/${path}`,
                { notes: resolveDialog.notes || null },
            );
            enqueueSnackbar(res.data?.message || 'Updated', { variant: 'success' });
            setResolveDialog({ ...resolveDialog, open: false });
            fetchBreaks();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                        letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                            <RuleIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white',
                                                          fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                IC Balance Reconciliation
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)',
                                              fontSize: '0.9rem', mt: 0.25 }}>
                                Detect and resolve unmatched / imbalanced intercompany items
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<RadarIcon />}
                            onClick={() => {
                                const today = new Date();
                                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
                                    .toISOString().slice(0, 10);
                                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                                    .toISOString().slice(0, 10);
                                setScanDialog({
                                    open: true,
                                    period_start: monthStart, period_end: monthEnd,
                                    tolerance: '0.01', purge_open: true,
                                });
                            }}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                  bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                        Run Scan
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)}
                                  sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Kind</InputLabel>
                        <Select value={kindFilter} label="Kind"
                                onChange={e => { setKindFilter(e.target.value); setPage(0); }}
                                sx={{ fontFamily, borderRadius: '10px' }}>
                            <MenuItem value="" sx={{ fontFamily }}>All</MenuItem>
                            {Object.keys(KIND_COLORS).map(k =>
                                <MenuItem key={k} value={k} sx={{ fontFamily }}>{k.replace('_', ' ')}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>

                <Tabs value={statusTab} onChange={(_, v) => { setStatusTab(v); setPage(0); }}
                      sx={{ mb: 2,
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none',
                                                fontSize: '0.85rem', color: '#64748b', minHeight: 40, py: 1 },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' } }}>
                    {STATUS_TABS.map(t => <Tab key={t} label={t} />)}
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Detected', 'Period', 'Kind', 'From → To', 'Primary',
                                      'Counterpart', 'Diff', 'Currency', 'Age', 'Status', 'Actions'].map(h =>
                                        <TableCell key={h} sx={labelCell}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [1,2,3].map(i => (
                                        <TableRow key={i}><TableCell colSpan={11}><Skeleton height={36} /></TableCell></TableRow>
                                    ))
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={11} sx={{ textAlign: 'center', py: 6 }}>
                                            <RuleIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>
                                                No breaks found
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>
                                                Click "Run Scan" to detect IC mismatches in a period
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(b => {
                                    const ss = STATUS_COLORS[b.status] || STATUS_COLORS.OPEN;
                                    const ks = KIND_COLORS[b.kind] || KIND_COLORS.OTHER;
                                    return (
                                        <TableRow key={b.id} hover>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {b.detected_at ? new Date(b.detected_at).toLocaleDateString() : '—'}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {b.period_start} → {b.period_end}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={b.kind?.replace('_', ' ')} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: ks.bg, color: ks.color }} />
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {b.from_entity_name || '—'} → {b.to_entity_name || '—'}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmt(b.primary_amount, b.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                {fmt(b.counterpart_amount, b.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                              fontWeight: 700, color: '#dc2626' }}>
                                                {fmt(b.difference, b.currency)}
                                            </TableCell>
                                            <TableCell sx={dataCell}>{b.currency}</TableCell>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600,
                                                              color: b.age_days > 60 ? '#dc2626'
                                                                  : b.age_days > 30 ? '#f59e0b' : '#475569' }}>
                                                {b.age_days}d
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={b.status} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: ss.bg, color: ss.color }} />
                                            </TableCell>
                                            <TableCell>
                                                {b.status === 'OPEN' && (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Resolve">
                                                            <IconButton size="small"
                                                                onClick={() => setResolveDialog({
                                                                    open: true, breakItem: b,
                                                                    action: 'resolve', notes: '',
                                                                })}
                                                                sx={{ color: '#16a34a' }}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Dismiss">
                                                            <IconButton size="small"
                                                                onClick={() => setResolveDialog({
                                                                    open: true, breakItem: b,
                                                                    action: 'dismiss', notes: '',
                                                                })}
                                                                sx={{ color: '#94a3b8' }}>
                                                                <BlockIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component="div" count={total} page={page}
                                     onPageChange={(_, p) => setPage(p)}
                                     rowsPerPage={rowsPerPage}
                                     onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                     sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }} />
                </Card>
            </Box>

            {/* Scan dialog */}
            <Dialog open={scanDialog.open}
                    onClose={() => setScanDialog({ ...scanDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Run Reconciliation Scan</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 1.5, borderRadius: '10px', fontFamily, fontSize: '0.78rem' }}>
                        Scans POSTED IC transactions in the period and detects unmatched / amount-mismatch / FX-difference breaks.
                    </Alert>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField label="Period Start *" type="date" size="small"
                                   value={scanDialog.period_start}
                                   onChange={e => setScanDialog(p => ({ ...p, period_start: e.target.value }))}
                                   InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Period End *" type="date" size="small"
                                   value={scanDialog.period_end}
                                   onChange={e => setScanDialog(p => ({ ...p, period_end: e.target.value }))}
                                   InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Tolerance" size="small" type="number"
                                   value={scanDialog.tolerance}
                                   onChange={e => setScanDialog(p => ({ ...p, tolerance: e.target.value }))}
                                   helperText="Max acceptable difference per pair (e.g. 0.01)"
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input type="checkbox" id="purge"
                                   checked={!!scanDialog.purge_open}
                                   onChange={e => setScanDialog(p => ({ ...p, purge_open: e.target.checked }))} />
                            <label htmlFor="purge"
                                   style={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                Replace existing OPEN breaks in this period
                            </label>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setScanDialog({ ...scanDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitScan}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Run Scan
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resolve / dismiss dialog */}
            <Dialog open={resolveDialog.open}
                    onClose={() => setResolveDialog({ ...resolveDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {resolveDialog.action === 'resolve' ? 'Resolve Break' : 'Dismiss Break'}
                </DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={3}
                               label={resolveDialog.action === 'resolve' ? 'Resolution notes *' : 'Notes (optional)'}
                               value={resolveDialog.notes}
                               onChange={e => setResolveDialog(p => ({ ...p, notes: e.target.value }))}
                               InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                               InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setResolveDialog({ ...resolveDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitResolve}
                            sx={{ fontFamily, textTransform: 'none',
                                  bgcolor: resolveDialog.action === 'resolve' ? '#16a34a' : '#94a3b8',
                                  '&:hover': { bgcolor: resolveDialog.action === 'resolve' ? '#15803d' : '#64748b' } }}>
                        {resolveDialog.action === 'resolve' ? 'Resolve' : 'Dismiss'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
